import type { MediaAssetDto } from '@/types/dtos/media/media.asset.dto';
import { hasProcessingAdvisories } from '@/helpers/media-processing-policy-messages.helper';

/** Full asset DTO or list-summary stub with optional soft-delete flag. */
export type ContributorRoleMediaAsset = MediaAssetDto | { deleted?: boolean };

export type ContributorMediaProcessingIssue = 'warning' | 'error';

/** Minimal media shape used across contributor dashboard (full MediaDto or list summary). */
export type ContributorRoleMediaItem = {
  id?: string;
  parent?: { id: string };
  parentType: string;
  isRequested?: boolean;
  assets?: ContributorRoleMediaAsset[];
  permissions?: string[];
  /** List-summary shortcut when full processingInsight is not loaded. */
  processingIssue?: ContributorMediaProcessingIssue;
};

/** Worst processing issue across the latest active asset of each media item. */
export const resolveContributorMediaProcessingIssue = (
  mediaItems: ContributorRoleMediaItem[],
): ContributorMediaProcessingIssue | undefined => {
  let hasWarning = false;

  for (const media of mediaItems) {
    if (media.processingIssue === 'error') {
      return 'error';
    }
    if (media.processingIssue === 'warning') {
      hasWarning = true;
    }

    const latestAsset = (media.assets ?? []).reduce<MediaAssetDto | undefined>((latest, asset) => {
      if (('deleted' in asset && asset.deleted === true) || !('version' in asset)) {
        return latest;
      }
      return !latest || asset.version > latest.version ? asset : latest;
    }, undefined);
    const insight = latestAsset?.processingInsight;
    if (!insight || insight.readiness === 'not_applicable') {
      continue;
    }
    if (insight.readiness === 'rejected' || insight.readiness === 'failed') {
      return 'error';
    }
    if ((insight.readiness === 'ok' || insight.playoutReady) && hasProcessingAdvisories(insight)) {
      hasWarning = true;
    }
  }

  return hasWarning ? 'warning' : undefined;
};

export const mediaHasUndeletedAssets = (media: ContributorRoleMediaItem): boolean => {
  if (!media.assets || media.assets.length === 0) return false;
  return media.assets.some((asset) => !('deleted' in asset && asset.deleted === true));
};

export const mediaCanUpload = (media: ContributorRoleMediaItem): boolean =>
  media.permissions?.includes('upload_asset') ?? false;

/** Requested slot still needs a file (matches role-details `requestsOpen`). */
export const mediaHasOpenRequestedUpload = (media: ContributorRoleMediaItem): boolean => {
  if (!media.isRequested || mediaHasUndeletedAssets(media)) return false;
  if (media.permissions === undefined) {
    // List summary DTO has no permissions — requested + empty is still actionable
    return true;
  }
  return mediaCanUpload(media);
};

/** Contributor's own non-requested media missing files (matches role-details `ownMissingAssets`). */
export const mediaHasOwnMissingUpload = (media: ContributorRoleMediaItem): boolean => {
  if (media.isRequested || mediaHasUndeletedAssets(media)) return false;
  if (media.permissions === undefined) {
    return false;
  }
  return mediaCanUpload(media);
};

export const roleMediaNeedsContributorUploadAction = (
  roleMedia: ContributorRoleMediaItem[],
): { hasOpenRequested: boolean; hasOwnMissing: boolean; needsUploadAction: boolean } => {
  const hasOpenRequested = roleMedia.some(mediaHasOpenRequestedUpload);
  const hasOwnMissing = roleMedia.some(mediaHasOwnMissingUpload);
  return {
    hasOpenRequested,
    hasOwnMissing,
    needsUploadAction: hasOpenRequested || hasOwnMissing,
  };
};

export type RoleForMediaMerge = {
  entityType: string;
  entityId?: string;
};

export const mergeRoleMedia = ({
  role,
  contributorMedia = [],
  entityMedia = [],
  eventId,
}: {
  role: RoleForMediaMerge;
  contributorMedia?: ContributorRoleMediaItem[];
  entityMedia?: ContributorRoleMediaItem[];
  eventId?: string;
}): ContributorRoleMediaItem[] => {
  let allMedia: ContributorRoleMediaItem[] = [...entityMedia];

  if (role.entityType === 'Event' || entityMedia.length === 0) {
    const fromContributor = contributorMedia.filter((media) => {
      if (media.parentType !== role.entityType) return false;
      if (role.entityType === 'Event') {
        return media.parent?.id === eventId || !eventId;
      }
      if (role.entityType === 'Flow' || role.entityType === 'Session') {
        return media.parent?.id === role.entityId || !role.entityId;
      }
      return false;
    });
    allMedia = [...allMedia, ...fromContributor];
  }

  const byId = new Map<string, ContributorRoleMediaItem>();
  for (const media of allMedia) {
    const key = 'id' in media && media.id ? media.id : `${media.parentType}:${media.parent?.id}:${media.isRequested}`;
    byId.set(key, media);
  }
  return Array.from(byId.values());
};
